/**
 * Night Shift Declaration List View (Calendar Layout)
 *
 * Renders a month-based calendar grid so users can visually see which days have
 * night-shift declarations.  Each day cell that has a declaration shows a
 * color-coded status chip (green = Approved, orange = Submitted, grey =
 * Cancelled).  Month navigation arrows let users browse past and future months.
 *
 * Preserved from the previous table implementation:
 * - Cancel confirmation dialog
 * - History dialog (`NightShiftDeclarationHistory`)
 * - "New Declaration" button in the page header
 * - Admin view displays the employee name inside each day cell
 *
 * @module night-shift-declarations/NightShiftDeclarationList
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Tooltip,
    IconButton,
    Paper,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNotify, usePermissions } from 'react-admin';
import {
    getNightShiftDeclarations,
    deleteNightShiftDeclaration,
    NightShiftDeclarationRecord,
} from '../dataProvider';
import { NightShiftDeclarationHistory } from './NightShiftDeclarationHistory';
import { buildCalendarGrid, DAY_LABELS } from './calendarUtils';

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/**
 * Checks whether a shift_date's month is the current month or in the future.
 *
 * @param shiftDate - ISO date string (YYYY-MM-DD).
 * @returns `true` if the date's month is the current month or later.
 */
function isCurrentOrFutureMonth (shiftDate: string): boolean {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const date = new Date(shiftDate + 'T00:00:00Z');
    const shiftYear = date.getUTCFullYear();
    const shiftMonth = date.getUTCMonth();

    if (shiftYear > currentYear) return true;
    if (shiftYear === currentYear && shiftMonth >= currentMonth) return true;
    return false;
}

/**
 * Returns a MUI Chip color based on the declaration status.
 *
 * @param status - The declaration status string ('Approved', 'Submitted', or 'Cancelled').
 * @returns A MUI chip color: `'success'` for Approved, `'warning'` for Submitted,
 *          `'default'` for Cancelled and any unknown status.
 */
function getStatusColor (status: string): 'success' | 'default' | 'warning' {
    switch (status) {
        case 'Approved': return 'success';
        case 'Cancelled': return 'default';
        case 'Submitted': return 'warning';
        default: return 'default';
    }
}

/**
 * Formats a year and zero-based month index into a human-readable string.
 *
 * @param year  - Full four-digit year.
 * @param month - Zero-based month index (0 = January … 11 = December).
 * @returns Locale-formatted month + year string, e.g. "March 2025".
 */
function formatMonthYear (year: number, month: number): string {
    return new Date(year, month, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Calendar-based list of night shift declarations.
 *
 * Fetches all declarations for the authenticated user (or all employees for
 * admin users) and overlays them on a monthly calendar grid.  Supports month
 * navigation, viewing declaration history, and cancelling eligible declarations.
 *
 * @returns The rendered calendar list component.
 */
export const NightShiftDeclarationList: React.FC = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const { permissions } = usePermissions<string[]>();

    // -----------------------------------------------------------------------
    // State
    // -----------------------------------------------------------------------

    const [declarations, setDeclarations] = useState<NightShiftDeclarationRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [historyDeclarationId, setHistoryDeclarationId] = useState<number | null>(null);

    // Calendar navigation: default to the current month
    const now = new Date();
    const [calendarYear, setCalendarYear] = useState(now.getFullYear());
    const [calendarMonth, setCalendarMonth] = useState(now.getMonth());

    const isAdmin = Array.isArray(permissions) && permissions.includes('night-shift-declarations.admin');

    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------

    /**
     * Fetches all night shift declarations from the API and stores them in state.
     */
    const fetchDeclarations = async () => {
        setLoading(true);
        try {
            const data = await getNightShiftDeclarations();
            setDeclarations(data);
        } catch (error) {
            console.error('Error fetching declarations:', error);
            notify('Failed to load declarations', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDeclarations();
    }, []); // Run only once on component mount

    // -----------------------------------------------------------------------
    // Cancel action
    // -----------------------------------------------------------------------

    /**
     * Cancels the declaration identified by {@link selectedId}.
     * Called when the user confirms in the delete dialog.
     */
    const handleDelete = async () => {
        if (!selectedId) return;

        try {
            await deleteNightShiftDeclaration(selectedId);
            notify('Declaration cancelled successfully', { type: 'success' });
            fetchDeclarations();
        } catch (_error) {
            notify('Failed to cancel declaration', { type: 'error' });
        } finally {
            setDeleteDialogOpen(false);
            setSelectedId(null);
        }
    };

    // -----------------------------------------------------------------------
    // Month navigation
    // -----------------------------------------------------------------------

    /**
     * Navigates the calendar one month backward.
     */
    const handlePrevMonth = () => {
        if (calendarMonth === 0) {
            setCalendarYear(y => y - 1);
            setCalendarMonth(11);
        } else {
            setCalendarMonth(m => m - 1);
        }
    };

    /**
     * Navigates the calendar one month forward.
     */
    const handleNextMonth = () => {
        if (calendarMonth === 11) {
            setCalendarYear(y => y + 1);
            setCalendarMonth(0);
        } else {
            setCalendarMonth(m => m + 1);
        }
    };

    // -----------------------------------------------------------------------
    // Calendar data
    // -----------------------------------------------------------------------

    /**
     * Builds a lookup map from ISO date string to the matching declaration
     * for the currently displayed month.
     *
     * @returns Record mapping `YYYY-MM-DD` → `NightShiftDeclarationRecord`.
     */
    const buildDateMap = (): Record<string, NightShiftDeclarationRecord> => {
        const monthStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}`;
        const map: Record<string, NightShiftDeclarationRecord> = {};
        for (const decl of declarations) {
            if (decl.shift_date.startsWith(monthStr)) {
                map[decl.shift_date] = decl;
            }
        }
        return map;
    };

    const dateMap = buildDateMap();

    // Build the calendar grid using the shared utility.
    // We pass an empty Set for existingDates because we use `dateMap` to
    // overlay declaration chips rather than the `isAlreadyDeclared` flag.
    const weeks = buildCalendarGrid(calendarYear, calendarMonth, new Set());

    // -----------------------------------------------------------------------
    // Loading state
    // -----------------------------------------------------------------------

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <Card>
            <CardContent>
                {/* Page header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">
                        Night Shift Declarations
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/night-shift-declarations/create')}
                    >
                        New Declaration
                    </Button>
                </Box>

                {/* Month navigation */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={2}
                    gap={2}
                    data-testid="month-navigation"
                >
                    <Tooltip title="Previous month">
                        <IconButton
                            onClick={handlePrevMonth}
                            size="small"
                            aria-label="Previous month"
                            data-testid="prev-month-btn"
                        >
                            <ArrowBackIosNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Typography variant="h6" data-testid="month-heading">
                        {formatMonthYear(calendarYear, calendarMonth)}
                    </Typography>

                    <Tooltip title="Next month">
                        <IconButton
                            onClick={handleNextMonth}
                            size="small"
                            aria-label="Next month"
                            data-testid="next-month-btn"
                        >
                            <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Day-of-week header row */}
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(7, 1fr)"
                    mb={0.5}
                    data-testid="calendar-header"
                >
                    {DAY_LABELS.map(label => (
                        <Box key={label} sx={{ textAlign: 'center', py: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold">
                                {label}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Calendar grid */}
                <Box data-testid="calendar-grid">
                    {weeks.map((week) => (
                        <Box
                            key={week.weekIndex}
                            display="grid"
                            gridTemplateColumns="repeat(7, 1fr)"
                            mb={0.5}
                        >
                            {week.days.map((dayEntry, idx) => {
                                const decl = dayEntry ? dateMap[dayEntry.date] : undefined;

                                return (
                                    <Paper
                                        key={idx}
                                        variant="outlined"
                                        sx={{
                                            minHeight: 72,
                                            p: 0.5,
                                            backgroundColor: dayEntry ? 'background.paper' : 'action.hover',
                                            cursor: decl ? 'pointer' : 'default',
                                        }}
                                        onClick={() => {
                                            if (decl) {
                                                setHistoryDeclarationId(decl.id);
                                                setHistoryDialogOpen(true);
                                            }
                                        }}
                                        data-testid={dayEntry ? `calendar-day-${dayEntry.date}` : `calendar-pad-${week.weekIndex}-${idx}`}
                                    >
                                        {dayEntry && (
                                            <Box>
                                                {/* Day number */}
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    display="block"
                                                    lineHeight={1.2}
                                                >
                                                    {dayEntry.day}
                                                </Typography>

                                                {decl && (
                                                    <Box mt={0.25}>
                                                        {/* Status chip */}
                                                        <Chip
                                                            label={decl.status}
                                                            color={getStatusColor(decl.status)}
                                                            size="small"
                                                            sx={{ fontSize: '0.6rem', height: 18, mb: 0.25 }}
                                                            data-testid={`status-chip-${dayEntry.date}`}
                                                        />

                                                        {/* Employee name (admin only) */}
                                                        {isAdmin && (
                                                            <Typography
                                                                variant="caption"
                                                                display="block"
                                                                noWrap
                                                                title={decl.full_name || decl.employee_id}
                                                                data-testid={`employee-name-${dayEntry.date}`}
                                                                sx={{ fontSize: '0.6rem' }}
                                                            >
                                                                {decl.full_name || decl.employee_id}
                                                            </Typography>
                                                        )}

                                                        {/* Cancel button — only for non-cancelled, current-or-future months */}
                                                        {decl.status !== 'Cancelled' && isCurrentOrFutureMonth(decl.shift_date) && (
                                                            <Tooltip title="Cancel declaration">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedId(decl.id);
                                                                        setDeleteDialogOpen(true);
                                                                    }}
                                                                    data-testid={`cancel-btn-${dayEntry.date}`}
                                                                    sx={{ p: 0.25 }}
                                                                    aria-label="Cancel declaration"
                                                                >
                                                                    <CancelIcon sx={{ fontSize: 14 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Box>
                    ))}
                </Box>

                {/* Empty state message */}
                {Object.keys(dateMap).length === 0 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Typography variant="body2" color="text.secondary" data-testid="empty-month-message">
                            No declarations for this month.
                        </Typography>
                    </Box>
                )}
            </CardContent>

            {/* Cancel confirmation dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Cancel Declaration</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to cancel this night shift declaration?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>No, Keep It</Button>
                    <Button onClick={handleDelete} color="error" autoFocus>
                        Yes, Cancel It
                    </Button>
                </DialogActions>
            </Dialog>

            {/* History dialog */}
            {historyDeclarationId !== null && (
                <NightShiftDeclarationHistory
                    declarationId={historyDeclarationId}
                    open={historyDialogOpen}
                    onClose={() => {
                        setHistoryDialogOpen(false);
                        setHistoryDeclarationId(null);
                    }}
                />
            )}
        </Card>
    );
};
